import { startCase } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { MfaBackup } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';
import { t, TranslationService } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Component({
  selector: 'sn-mfa-backup-codes-dialog',
  templateUrl: './mfa-backup-codes-dialog.component.html',
  styleUrls: ['./mfa-backup-codes-dialog.component.scss'],
})
export class MfaBackupCodesDialogComponent implements OnInit, OnDestroy {
  public form = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [
    {
      key: 'password',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'common.password',
        'common.enterPassword',
        {
        type: 'password',
        required: true,
        toggleMask: true,
        attributes: {
          autocomplete: 'new-password',
        },
        }
      ),
    },
  ];
  public backupCodes: MfaBackup;
  public copyContent;
  public isLoading: boolean = false;
  public isConfirmed: boolean = false;
  public isGenerating: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SnCurrentUser,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _dialogService: SnDialogService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _translationService: TranslationService,
  ) {
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public isArray(value: any): boolean {
    return Array.isArray(value);
  }

  public toStartCase(text: string): string {
    return startCase(text);
  }

  public checkBackupCodes(): void {
    if (this.form.invalid) {
      return;
    }
    this.isLoading = true;
    this._usersAPI
      .getBackupCodes(this.data.id, {
        password: this.formModel.password,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (codes) => {
          if (codes.tokens?.length) {
            this.backupCodes = codes;
            this.copyContent = this.backupCodes.tokens.join('\n');
            this.isLoading = false;
            this.isConfirmed = true;
          } else {
            this._genBackupCodes();
          }
        },
        error: (err) => {
          this.form.setErrors(err.error);
          this.isLoading = false;
          this._cd.detectChanges();
        },
      });
    this._cd.detectChanges();
  }

  public print(): void {
    const content = document.querySelector('.printContent').innerHTML;
    const popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    popupWin.document.open();
    popupWin.document.write(`
      <html>
        <head>
          <title>${t('common.backupCodes')}</title>
        </head>
        <body onload="window.print();window.close()">${content}</body>
      </html>`);
    popupWin.document.close();
  }

  public regenerateCodes(): void {
    this._dialogService
      .confirm({
        message:
          'account.dialogs.mfa.backupCodes.confirmGenerateMessage',
        confirmButton: {
          text: 'account.dialogs.mfa.backupCodes.generateNewCodes',
          color: 'accent',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._genBackupCodes();
      });
  }

  private _genBackupCodes(): void {
    this.isGenerating = true;
    this._usersAPI
      .genBackupCodes(this.data.id, {
        password: this.formModel.password,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (codes) => {
          this.backupCodes = codes;
          this.copyContent = this.backupCodes.tokens.join('\n');
          this.isLoading = false;
          this.isConfirmed = true;
          this.isGenerating = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.form.setErrors(err.error);
          this.isGenerating = false;
        },
      });
  }
}
